import he from 'he';
import { Box, Link, Typography } from '@mui/material';
import sanitizeHtml from 'sanitize-html';
import { useActiveTheme } from '@care/react-component-lib';
import { useResponsive } from '@/components/hooks/useResponsive';
import dateMonthYearFormat from '@/utils/dateMonthYearFormat';
import { trackAmplitudeEvent, AmplitudeEvent } from '@/lib/analyticsHelper';
import {
  seoArticlesList_seoArticlesList_SEOArticlesListSuccess_seoArticlesList_articles as SEOArticle,
  seoArticlesList_seoArticlesList_SEOArticlesListSuccess_seoArticlesList_articles_largeImage as SEOArticleImage,
} from '@/__generated__/seoArticlesList';

export interface ArticleCardProps {
  imageUrl: NonNullable<SEOArticleImage['imageUrl']>;
  title: SEOArticle['title'];
  url: SEOArticle['url'];
  description: SEOArticle['description'];
  articleCreateDate: SEOArticle['createDate'];
  slotProps?: Record<string, Record<'data-testid', string>>;
}

export const ArticleCard = ({
  imageUrl,
  title,
  url,
  description,
  articleCreateDate,
  slotProps = {},
}: ArticleCardProps) => {
  const { isCareTheme } = useActiveTheme();
  const { isSmallViewport, isLargeViewport } = useResponsive();
  // remove the <p> tag from the description
  const previewDescription = description ? `${description.replace(/<\/?p>/gi, '')}...` : null;
  const articleDate =
    articleCreateDate !== null
      ? dateMonthYearFormat({
          dateTime: articleCreateDate,
          useOrdinalDay: false,
          useLongMonth: true,
          useFullDate: true,
        })
      : null;

  const amplitudeEventCardClick = () => {
    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
      feature_name: 'Clicked Article Carousel',
    });
  };

  return (
    <Box>
      <Link
        {...slotProps?.link}
        onClick={amplitudeEventCardClick}
        href={url}
        target={!isSmallViewport ? '_blank' : '_self'}
        variant="link2"
        sx={(theme) => ({
          color: theme.palette.text.primary,
          textDecoration: 'none',
          WebkitTapHighlightColor: 'transparent',
          '&:hover': {
            color: isCareTheme ? theme.palette.care.navy?.[700] : undefined,
            cursor: 'pointer',
            textDecoration: 'none',
          },
        })}>
        <Box
          {...slotProps?.image}
          mb={{ xs: 1, sm: 2 }}
          borderRadius="24px"
          component="img"
          src={imageUrl}
          alt={title}
          loading="lazy"
        />
        <Box display="flex" flexDirection="column">
          <Typography {...slotProps?.title} component="h3" variant="h3" mb={1}>
            {he.decode(title)}
          </Typography>
          {previewDescription && (
            <Typography
              {...slotProps?.description}
              component="p"
              variant={isLargeViewport ? 'body2' : 'body3'}
              color="text.secondary"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  he.decode(
                    articleDate ? `${articleDate} - ${previewDescription}` : previewDescription
                  )
                ),
              }}
            />
          )}
        </Box>
      </Link>
    </Box>
  );
};
